import React, { useState } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import logo from '../assets/logo.png';
import ThemeToggle from './common/ThemeToggle';

export default function Login({ onLogin, serverError }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Registreren: nieuw bedrijf. Het account ontstaat hier; het bedrijf en
    // het profiel ontstaan daarna in het Onboarding-scherm, na e-mailbevestiging.
    const [registreren, setRegistreren] = useState(false);
    const [wachtwoord2, setWachtwoord2] = useState('');

    const handleRegistreer = async (e) => {
        e.preventDefault();
        setError('');
        if (!username.includes('@')) {
            setError('Registreren kan alleen met een e-mailadres.');
            return;
        }
        if (password.length < 8) {
            setError('Gebruik een wachtwoord van minimaal 8 tekens.');
            return;
        }
        if (password !== wachtwoord2) {
            setError('De wachtwoorden zijn niet gelijk.');
            return;
        }
        setLoading(true);
        try {
            // Onthoud de intentie voor het Onboarding-scherm: wie hier
            // registreert, komt een nieuw bedrijf starten en hoeft die keuze
            // daar niet nogmaals te maken.
            sessionStorage.setItem('rw_onboarding_keuze', 'bedrijf');
            const cred = await createUserWithEmailAndPassword(auth, username.trim(), password);
            // Vast versturen; mislukt dit, dan kan het opnieuw in Onboarding.
            try { await sendEmailVerification(cred.user); } catch (_) { }
            // Verder gaat vanzelf: de auth-status verandert en de app toont
            // het Onboarding-scherm.
        } catch (err) {
            console.error('Registratie mislukt:', err.code, err.message);
            if (err.code === 'auth/email-already-in-use') {
                setError('Dit e-mailadres heeft al een account. Log in.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Dit is geen geldig e-mailadres.');
            } else if (err.code === 'auth/weak-password') {
                setError('Dit wachtwoord is te zwak.');
            } else if (err.code === 'auth/operation-not-allowed' || err.code === 'auth/admin-restricted-operation') {
                setError('Registreren staat op dit moment uit. Neem contact op via info@raconsoft.nl.');
            } else {
                setError('Er is een fout opgetreden: ' + err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Construct email from username (assuming internal domain if no @)
            const email = username.includes('@') ? username : `${username}@raconsoft.nl`;

            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            // onLogin is handled by onAuthStateChanged in App.jsx, but we can call it here if needed for immediate feedback
            // However, App.jsx will react to the auth state change automatically.

        } catch (error) {
            console.error('Login failed:', error.code, error.message);
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                setError('Ongeldige gebruikersnaam of wachtwoord');
            } else if (error.code === 'auth/too-many-requests') {
                setError('Te veel inlogpogingen. Probeer het later opnieuw.');
            } else {
                setError('Er is een fout opgetreden: ' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative">
            <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                <ThemeToggle />
            </div>
            <div className="card w-full max-w-md p-8 fade-in">
                <div className="text-center mb-8">
                    <img src={logo} alt="Logo" className="login-logo" />
                    <h1 className="text-2xl font-bold text-white mb-2">RaconWorks</h1>
                    <p className="text-slate-400">{registreren ? 'Nieuw account aanmaken' : 'Log in om verder te gaan'}</p>
                </div>

                <form onSubmit={registreren ? handleRegistreer : handleSubmit} className="space-y-6">
                    <div>
                        <label className="label">{registreren ? 'E-mailadres' : 'Gebruikersnaam'}</label>
                        <input
                            type="text"
                            className="input glass-input"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder={registreren ? "naam@bedrijf.nl" : "Voer gebruikersnaam in..."}
                        />
                    </div>

                    <div>
                        <label className="label">Wachtwoord</label>
                        <div className="relative" style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                className="input glass-input pr-10"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Voer wachtwoord in..."
                            />
                            <button
                                type="button"
                                style={{
                                    position: 'absolute',
                                    right: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '4px',
                                    zIndex: 10
                                }}
                                className="text-slate-400 hover:text-white transition-colors outline-none"
                                onClick={() => setShowPassword(!showPassword)}
                                tabIndex="-1"
                            >
                                {showPassword ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                    {registreren && (
                        <div>
                            <label className="label">Wachtwoord (herhaling)</label>
                            <input
                                type="password"
                                className="input glass-input"
                                value={wachtwoord2}
                                onChange={(e) => setWachtwoord2(e.target.value)}
                                placeholder="Zelfde wachtwoord nogmaals..."
                            />
                        </div>
                    )}
                    {error && (
                        <div className="px-4 py-3 rounded text-sm text-center" style={{ background: 'var(--color-danger-weak)', color: 'var(--color-danger)', border: '1px solid var(--color-danger-weak)' }}>
                            {error}
                        </div>
                    )}
                    {serverError && (
                        <div className="px-4 py-3 rounded text-sm text-center" style={{ background: 'var(--color-warning-weak)', color: 'var(--color-warning)', border: '1px solid var(--color-warning-weak)' }}>
                            {serverError}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary w-full py-3 text-lg"
                    >
                        {loading ? 'Bezig...' : (registreren ? 'Account aanmaken' : 'Inloggen')}
                    </button>
                </form>

                <button
                    type="button"
                    onClick={() => { setRegistreren(!registreren); setError(''); }}
                    className="w-full text-center text-sm text-slate-400 hover:text-white mt-6 transition-colors"
                >
                    {registreren
                        ? 'Al een account? Log in'
                        : 'Nieuw bedrijf? Maak een account aan'}
                </button>
            </div>
        </div>
    );
}
